/**
 * RoundSummary — visible while the server's round Continue gate is active
 * (v1.2 Phase 14, RULES §7).
 *
 * The engine has already auto-advanced into the next round's bidding, but
 * the server suppresses that broadcast (synthetic paused view sets
 * `view.gateKind === "round"`, hides yourHand, zeros handCounts). This
 * component reads the just-completed round from `view.ledger[last]` and
 * surfaces:
 *
 *   - Round number + bid + trump + contract team.
 *   - Trick counts + deltas per team.
 *   - Per-PLAYER outcome banner ("You won this round!" / "You lost." /
 *     "Push.") computed from `view.you`'s team vs `result.winner`.
 *   - Forfeit info (RULES §6) when present on the ledger row.
 *   - A Continue button + a "Waiting for N more…" hint sourced from
 *     `view.roundAcks` vs `view.handCounts.length` (the full seat count).
 *
 * The gate clears the instant every connected human has acked; the
 * server then broadcasts the real next-round state and this component
 * unmounts (gateKind becomes null).
 *
 * Motion scale-in on the outcome header gives the round-end summary a
 * deliberate beat without competing with the trick sweep that just
 * happened — Phase 14 plan's "Motion scale-in for the outcome header."
 */
import { m } from "motion/react";
import { actions } from "../wire/index.js";
import { useView } from "../state/hooks.js";

const SUIT_GLYPH: Record<"C" | "D" | "H" | "S", string> = {
  C: "♣",
  D: "♦",
  H: "♥",
  S: "♠",
};
const SUIT_NAME: Record<"C" | "D" | "H" | "S", string> = {
  C: "clubs",
  D: "diamonds",
  H: "hearts",
  S: "spades",
};

function suitClass(suit: "C" | "D" | "H" | "S"): string {
  return suit === "D" || suit === "H" ? "suit-red" : "suit-black";
}

/** RULES §1.3: even seats = Team A, odd seats = Team B. */
function teamForSeat(seat: number): "A" | "B" {
  return seat % 2 === 0 ? "A" : "B";
}

export function RoundSummary(): JSX.Element | null {
  const view = useView();
  if (!view) return null;
  // v1.2 Phase 14: the round gate is the ONLY trigger for this overlay.
  // The previous `view.phase === "round_end"` gate is replaced by the
  // explicit `view.gateKind === "round"` check — the engine no longer
  // sits at `round_end` (it auto-advances), and the server uses the
  // gate field instead.
  if (view.gateKind !== "round") return null;

  // The last entry in the ledger is the round that just finished.
  const result = view.ledger[view.ledger.length - 1];
  if (!result) return null;

  const tricksA = result.tricks.A ?? 0;
  const tricksB = result.tricks.B ?? 0;
  const deltaA = result.delta.A ?? 0;
  const deltaB = result.delta.B ?? 0;
  const fmtDelta = (d: number): string => (d > 0 ? `+${d}` : `${d}`);

  const youTeam = teamForSeat(view.you);
  const outcome: "won" | "lost" | "push" =
    result.winner === null
      ? "push"
      : result.winner === youTeam
        ? "won"
        : "lost";
  const outcomeLabel =
    outcome === "won"
      ? "You won this round!"
      : outcome === "lost"
        ? "You lost."
        : "Push — neither team made bid.";

  const acks = view.roundAcks ?? [];
  const totalSeats = view.handCounts.length;
  // "Waiting for N more" counts ONLY humans not yet acked. We don't have
  // a direct humans-only field, so derive: seats not in acks. Bots are
  // auto-acked at gate entry so they're already in `acks` — the
  // remainder are the humans we're waiting on.
  const seatsNotAcked: number[] = [];
  for (let s = 0; s < totalSeats; s++) {
    if (!acks.includes(s)) seatsNotAcked.push(s);
  }
  const youAcked = view.youAcked ?? acks.includes(view.you);
  const waitingForCount = seatsNotAcked.length;
  const waitingHint =
    waitingForCount === 0
      ? "Advancing…"
      : waitingForCount === 1
        ? "Waiting for 1 player…"
        : `Waiting for ${waitingForCount} players…`;

  const handleContinue = (): void => {
    if (!youAcked) actions.continueRound();
  };

  return (
    <div
      className="round-summary-overlay"
      role="dialog"
      aria-labelledby="round-summary-title"
      aria-modal="true"
    >
      <div className="round-summary-card">
        <m.h2
          id="round-summary-title"
          initial={{ scale: 0.85, opacity: 0 }}
          animate={{ scale: 1, opacity: 1 }}
          transition={{ duration: 0.32, ease: "easeOut" }}
        >
          Round {result.round + 1} — complete
        </m.h2>
        <p
          className={`round-summary-outcome round-summary-outcome-${outcome}`}
          aria-live="polite"
        >
          {outcomeLabel}
        </p>
        <div className="round-summary-row">
          <span className="round-summary-label">Bid</span>
          <span className="round-summary-value">
            {result.bid}
            <span
              className={`hud-value-suit hud-suit ${suitClass(result.trump)}`}
              aria-label={SUIT_NAME[result.trump]}
            >
              {SUIT_GLYPH[result.trump]}
            </span>
            <span className="round-summary-meta">
              by Team {result.contractTeam}
            </span>
          </span>
        </div>
        <div className="round-summary-row">
          <span className="round-summary-label">Hands</span>
          <span className="round-summary-value">
            <span
              className={`round-team-pill ${result.contractTeam === "A" ? "is-contract" : ""}`}
            >
              A <strong>{tricksA}</strong> ({fmtDelta(deltaA)})
            </span>
            <span
              className={`round-team-pill ${result.contractTeam === "B" ? "is-contract" : ""}`}
            >
              B <strong>{tricksB}</strong> ({fmtDelta(deltaB)})
            </span>
          </span>
        </div>
        <div className="round-summary-row">
          <span className="round-summary-label">Outcome</span>
          <span className="round-summary-value">
            {result.winner ? (
              <strong className="round-winner">Team {result.winner} wins</strong>
            ) : (
              <strong className="round-winner round-winner-push">Push</strong>
            )}
          </span>
        </div>
        {result.forfeited ? (
          <div className="round-summary-row round-summary-forfeit">
            <span className="round-summary-label">Forfeit</span>
            <span className="round-summary-value">
              Seat {result.forfeited.strandedSeat} stranded on joker(s) —{" "}
              {result.forfeited.count} hand
              {result.forfeited.count === 1 ? "" : "s"} forfeited.
            </span>
          </div>
        ) : null}
        <div className="round-summary-actions">
          <button
            type="button"
            className="round-summary-continue"
            onClick={handleContinue}
            disabled={youAcked}
            aria-label={youAcked ? "Waiting for other players" : "Continue to next round"}
          >
            {youAcked ? "Waiting…" : "Continue"}
          </button>
          <p className="round-summary-waiting" aria-live="polite">
            {waitingHint}
          </p>
        </div>
      </div>
    </div>
  );
}
